/**
 * TaskChangeSection - Section wrapper for task change cards.
 *
 * Sorts: created → updated → deleted → ignored.
 * Groups task updates by task_id.
 */

import { useCallback, useMemo } from 'react';

import { TaskDetail } from '@/components/tasks/task-detail/TaskDetail';
import { Badge } from '@/components/ui/catalyst/badge';
import type { EntityChangeSchema } from '@/services/api/generated/entityChangesService';

import { CHANGE_TYPE_ORDER } from './changeTypeConfig';
import { useDetailDrawer } from './DetailDrawerContext';
import { TaskChangeCard } from './TaskChangeCard';
import { TaskUpdateChangeCard } from './TaskUpdateChangeCard';

interface TaskChangeSectionProps {
  tasks: EntityChangeSchema[];
  taskUpdates?: EntityChangeSchema[];
  matterId: string;
}

export const TaskChangeSection: React.FC<TaskChangeSectionProps> = ({
  tasks,
  taskUpdates = [],
  matterId,
}) => {
  const { openDrawer, closeDrawer } = useDetailDrawer();

  const handleTaskClick = useCallback(
    (entityId: string) => {
      openDrawer(
        <TaskDetail
          taskId={entityId}
          matterId={matterId}
          onClose={closeDrawer}
          isModal={false}
        />,
      );
    },
    [matterId, openDrawer, closeDrawer],
  );
  const sorted = useMemo(
    () =>
      [...tasks].sort(
        (a, b) =>
          CHANGE_TYPE_ORDER[a.change_type] -
          CHANGE_TYPE_ORDER[b.change_type],
      ),
    [tasks],
  );

  // Group task updates by task_id from snapshot data
  const tuByTask = useMemo(
    () =>
      taskUpdates.reduce<Record<string, EntityChangeSchema[]>>((map, tu) => {
        const snapshot =
          tu.change_type === 'deleted' ? tu.old_snapshot : tu.new_snapshot;
        const taskId = (snapshot?.data as Record<string, unknown>)?.task_id as
          | string
          | undefined;
        if (taskId) {
          (map[taskId] ??= []).push(tu);
        }
        return map;
      }, {}),
    [taskUpdates],
  );

  // Task updates whose parent task is not in the tasks list (e.g. backfill
  // failed or parent was filtered out).  Render them standalone so they are
  // never silently hidden.
  const taskEntityIdSet = useMemo(
    () => new Set(tasks.map((t) => t.entity_id)),
    [tasks],
  );

  const orphanedTaskUpdates = useMemo(
    () =>
      Object.entries(tuByTask)
        .filter(([taskId]) => !taskEntityIdSet.has(taskId))
        .flatMap(([, updates]) => updates),
    [tuByTask, taskEntityIdSet],
  );

  // Count only tasks with real changes (not context-only backfills)
  const actionableCount =
    tasks.filter((t) => t.change_type !== 'ignored').length +
    orphanedTaskUpdates.filter((tu) => tu.change_type !== 'ignored').length;

  if (tasks.length === 0 && orphanedTaskUpdates.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="text-base font-semibold text-gray-900">Tasks</h3>
        {actionableCount > 0 && (
          <Badge color="zinc" className="text-xs">
            {actionableCount}
          </Badge>
        )}
      </div>
      <div className="space-y-2">
        {sorted.map((task) => (
          <TaskChangeCard
            key={task.entity_id}
            entity={task}
            taskUpdates={tuByTask[task.entity_id]}
            matterId={matterId}
            onNameClick={() => handleTaskClick(task.entity_id)}
          />
        ))}
        {orphanedTaskUpdates.map((tu) => (
          <TaskUpdateChangeCard key={tu.id} entity={tu} matterId={matterId} />
        ))}
      </div>
    </div>
  );
};
